import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BusinessDetailsService,
  ComponentBase,
  LanguageComponent,
  SharedService,
} from 'src/app/core';
import { VideoComponent } from 'src/app/shared/video/video.component';
import { AutoRegistrationTopBarComponent } from '../../shared/auto-registration-top-bar/auto-registration-top-bar.component';
import { BasicDetailsStepComponent } from '../registration-steps/basic-details-step/basic-details-step.component';
import { BusinessDetailsStepComponent } from '../registration-steps/business-details-step/business-details-step.component';
import { WorkingHoursStepComponent } from '../registration-steps/working-hours-step/working-hours-step.component';
import { takeUntil } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { BasicDetailsStep2Component } from './basic-details-step2/basic-details-step2.component';
import { BusinessDetailsStep2Component } from './business-details-step2/business-details-step2.component';


@Component({
  selector: 'app-registration-steps2',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    AutoRegistrationTopBarComponent,
    MatStepperModule,
    MatButtonModule,
    BasicDetailsStepComponent,
    BasicDetailsStep2Component,
    BusinessDetailsStepComponent,
    BusinessDetailsStep2Component,
    WorkingHoursStepComponent,
    VideoComponent,
    LanguageComponent,
  ],
  templateUrl: './registration-steps2.component.html',
  styleUrl: './registration-steps2.component.scss'
})
export class RegistrationSteps2Component extends ComponentBase {

  /** Current UI direction (ltr/rtl) from locale */
  dir: string = localStorage.getItem('lang') || '';

  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('stepper', { read: ElementRef }) private stepperHost?: ElementRef<HTMLElement>;
  @ViewChild(BasicDetailsStepComponent) basicDetailsStepCmp?: BasicDetailsStepComponent;

  /** True when user navigated back from Working Hours step to Business Details; used to show Skip as disabled with a "Done Skip Before" tooltip. */
  isBusinessDetailsFromPrevious: boolean = false;
  /** Currently active step index (0-based) */
  selectedIndex = 0;

  /** Indices of steps that are completed (based on backend phases) */
  completedSteps: number[] = [];
  totalSteps = 3;

  screenWidth: number = window.innerWidth;

  get isFirstStep(): boolean {
    return this.selectedIndex === 0;
  }

  get isLastStep(): boolean {
    return this.selectedIndex === this.totalSteps - 1;
  }

  get canGoNext(): boolean {
    return !this.isLastStep;
  }

  get canGoPrevious(): boolean {
    return !this.isFirstStep;
  }

  constructor(
    private readonly dialog: MatDialog,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly sharedService: SharedService,
    public readonly translateService: TranslateService,
    readonly title: Title,
  ) {
    super();
    translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.dir = _.lang;
      // title.setTitle(translateService.instant('Title_phase_one'));
    });
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.loadRegisteredPhases();
  }

  ngAfterViewInit(): void {
    this.setCompletedStepClass();
  }

  /**
   * Loads registered phases once and:
   * - Decides which step should be active (selectedIndex)
   * - Marks previous steps as completed
   *
   * Rules:
   * - If Result.Two exists  -> show step 2 (BUSINESS_DETAILS), mark step 1 as completed
   * - If Result.Three exists -> show step 3 (WORKING_HOUR), mark steps 1 & 2 as completed
   */
  private loadRegisteredPhases(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');

    if (!regId) return;

    this.businessDetailsService
      .getRegisteredPhases({ RegistrationCycleLogId: +regId })
      .subscribe((res) => {
        const result = res?.Result;
        if (!result) return;

        // Handle phase Two (first step already done)
        const two = result.Two;
        if (two) {
          // Step 0 (basic details) is completed when backend has Two object
          this.markStepCompleted(0);

          // By default, if we only have Two, we should be on step 1 (BUSINESS_DETAILS)
          this.goToIndex(1);
        }

        // Handle phase Three (second step already done or skipped)
        const three = result.Three;
        if (three) {
          // Steps 0 & 1 are completed when backend has Three object
          this.markStepCompleted(0);
          this.markStepCompleted(1);

          // If Three exists, we should be on step 2 (WORKING_HOUR)
          this.goToIndex(2);
        }
        this.setCompletedStepClass();
      });
  }

  onBasicDetailsNext(): void {
    this.markStepCompleted(0);
    this.goToIndex(1);
  }

  onBusinessDetailsNext(event: any): void {
    if (event?.doneBusinessDetailsBySkip) {
      this.loadRegisteredPhases();
    }
    this.markStepCompleted(1);
    this.goToIndex(2);
  }

  onBusinessDetailsPrevious(): void {
    this.goToIndex(0);
  }

  onWorkingHoursPrevious(): void {
    this.isBusinessDetailsFromPrevious = true;
    this.goToIndex(1);
  }

  onOpenVideo(): void {
    this.dialog.open(VideoComponent, {
      minWidth: '60%',
      maxWidth: '100%',
      panelClass: 'aspect_ratio',
    });
  }

  isStepCompleted(stepIndex: number): boolean {
    return this.completedSteps.includes(stepIndex);
  }

  isStepActive(stepIndex: number): boolean {
    return this.selectedIndex === stepIndex;
  }

  private markStepCompleted(stepIndex: number): void {
    if (!this.completedSteps.includes(stepIndex)) {
      this.completedSteps.push(stepIndex);
    }
  }

  private goToIndex(index: number): void {
    this.selectedIndex = index;
    if (this.stepper) this.stepper.selectedIndex = index;
    this.setCompletedStepClass();
  }

  private setCompletedStepClass(): void {
    const root = this.stepperHost?.nativeElement;
    if (!root) return;
    
    root.querySelectorAll('.mat-vertical-content-container').forEach((el) => {
      el.classList.remove('completed-step');
    });

    root.querySelectorAll<HTMLElement>('.mat-vertical-content-container').forEach((el, index) => {
      if (this.isStepCompleted(index)) {
        el.classList.add('completed-step');
      }
    });

    root.querySelectorAll('.mat-step').forEach((el) => {
      el.classList.remove('completed-step');
    });
    root.querySelectorAll<HTMLElement>('.mat-step').forEach((el, index) => {
      if (this.isStepCompleted(index)) {
        el.classList.add('completed-step');
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenWidth = event.target.innerWidth;
  }
}
